/**
 * Flexible Key Results (Roadmap V-06): Zahl mit Richtung, Ja/Nein, Prozent,
 * Anzahl - fuer jeden Lebensbereich, nicht nur Sport.
 *
 * Jede Art wird auf Start/Ist/Ziel abgebildet; die Fortschrittsformel in
 * `progress.ts` bleibt dieselbe und rechnet "Senken" schon richtig. Hier
 * stehen nur die Regeln je Art und die Texte fuer die Anzeige.
 */
import type { KeyResult, KeyResultDirection, KeyResultType, LocalDate } from '@/domain/types';

export const KR_TYPES: { value: KeyResultType; label: string; hinweis: string }[] = [
  { value: 'zahl', label: 'Zahl', hinweis: 'Von einem Wert auf einen anderen, Einheit frei, z. B. € , Seiten, Notenpunkte' },
  { value: 'anzahl', label: 'Anzahl', hinweis: 'Wie oft etwas erledigt wurde, z. B. 10 Kundengespräche' },
  { value: 'prozent', label: 'Prozent', hinweis: 'Direkter Fortschritt von 0 bis 100 %' },
  { value: 'janein', label: 'Ja/Nein', hinweis: 'Erreicht oder nicht, z. B. „Führerschein bestanden“' },
];

export function krType(kr: Pick<KeyResult, 'type'>): KeyResultType {
  return kr.type ?? 'zahl';
}

/** Richtung einer Zahl: ausdruecklich gewaehlt, sonst aus Start und Ziel. */
export function krDirection(kr: Pick<KeyResult, 'direction' | 'start' | 'target'>): KeyResultDirection {
  return kr.direction ?? (kr.target < kr.start ? 'senken' : 'steigern');
}

/** Widerspricht die gewaehlte Richtung den Werten? Dann warnt der Dialog. */
export function richtungPasstNicht(kr: KeyResult): boolean {
  if (krType(kr) !== 'zahl' || !kr.direction) return false;
  return kr.direction === 'senken' ? kr.target >= kr.start : kr.target <= kr.start;
}

/**
 * Bringt Start/Ziel/Einheit in die Form, die die Art verlangt. Beim
 * Wechsel der Art und vor dem Speichern angewendet.
 */
export function normalisiereKr(kr: KeyResult): KeyResult {
  switch (krType(kr)) {
    case 'janein':
      return { ...kr, start: 0, target: 1, current: kr.current >= 1 ? 1 : 0, unit: '', direction: undefined };
    case 'prozent':
      return { ...kr, start: 0, target: 100, current: Math.min(100, Math.max(0, kr.current)), unit: '%', direction: undefined };
    case 'anzahl':
      return { ...kr, start: 0, target: Math.max(1, Math.round(kr.target)), current: Math.max(0, Math.round(kr.current)), direction: undefined };
    default:
      return kr;
  }
}

/** Kurzer Text fuer die Karte, z. B. "500 → 300 € senken", "3 von 10 Gespräche". */
export function krBeschreibung(kr: KeyResult): string {
  const einheit = kr.unit ? ` ${kr.unit}` : '';
  switch (krType(kr)) {
    // Der Stand selbst steht am Haekchen daneben.
    case 'janein': return 'Ja/Nein';
    case 'prozent': return `Ziel 100 %`;
    case 'anzahl': return `Ziel ${kr.target}${einheit}`;
    default: return `${kr.start} → ${kr.target}${einheit} ${krDirection(kr) === 'senken' ? 'senken' : 'steigern'}`;
  }
}

/**
 * Traegt den neuen Ist-Wert in den Verlauf ein - hoechstens ein Eintrag je
 * Tag, der letzte Wert des Tages gilt.
 */
export function mitMessung(kr: KeyResult, value: number, tag: LocalDate): KeyResult {
  const ohneHeute = (kr.history ?? []).filter((m) => m.date !== tag);
  return { ...kr, current: value, history: [...ohneHeute, { date: tag, value }].sort((a, b) => a.date.localeCompare(b.date)) };
}
